import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useMonthlyConfirmations } from "@/hooks/use-monthly-confirmations";
import { useTimeGaps } from "@/hooks/use-time-gaps";
import { useUserCapacity } from "@/hooks/use-user-capacity";
import { SrIcon } from "@/components/ui/SrIcon";
import { cn } from "@/lib/utils";
import { getMonthAttestationReminderLevel } from "@/lib/month-attestation";

const MONTH_NAMES = [
  "",
  "januar",
  "februar",
  "mars",
  "april",
  "mai",
  "juni",
  "juli",
  "august",
  "september",
  "oktober",
  "november",
  "desember",
];

export default function CloseMonthBanner({
  userId,
  year,
}: {
  userId: string | null;
  year: number;
}) {
  const navigate = useNavigate();
  const { data: confirmations = [] } = useMonthlyConfirmations(userId, year);
  const { data: capacity = 0 } = useUserCapacity(userId);
  const now = new Date();
  const prevMonth = now.getMonth() === 0 ? 12 : now.getMonth();
  const prevYear = now.getMonth() === 0 ? year - 1 : year;

  const prevMonthConfirmed = confirmations.some(
    (c) => c.month === prevMonth && c.year === prevYear
  );

  // Get gap data for previous month
  const prevMonthDate = new Date(prevYear, prevMonth - 1, 15);
  const { data: gapData } = useTimeGaps(userId, prevMonthDate);

  // Kun ansatte med fakturerbar tid attesterer (kohort = kapasitet > 0).
  if (capacity <= 0) return null;
  if (prevMonthConfirmed) return null;

  const monthName =
    MONTH_NAMES[prevMonth]?.charAt(0).toUpperCase() +
    MONTH_NAMES[prevMonth]?.slice(1);
  const gapDays = gapData?.gapDays ?? 0;
  const reminderLevel = getMonthAttestationReminderLevel(now.getDate());
  const isAvailable = reminderLevel === "available";
  const isProminent = reminderLevel === "prominent";
  const reminderText = isAvailable
    ? "er klar for lukking."
    : isProminent
      ? "må lukkes."
      : "er ikke lukket ennå.";

  return (
    <div
      data-testid="month-attestation-reminder"
      data-level={reminderLevel}
      className={cn(
        "flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-4 py-3 rounded-lg border mb-6",
        isAvailable && "border-border bg-muted/30",
        reminderLevel === "normal" && "border-amber-200 bg-amber-50/70",
        isProminent && "border-amber-400 bg-amber-100",
      )}
    >
      <div className="flex items-start md:items-center gap-2.5 min-w-0">
        <SrIcon
          name={isAvailable ? "calendar-days" : "alert-circle"}
          className={cn(
            "w-4.5 h-4.5 flex-shrink-0 mt-0.5 md:mt-0",
            isAvailable ? "text-muted-foreground" : "text-amber-700",
          )}
        />
        <p className={cn("text-sm", isAvailable ? "text-foreground" : "text-amber-950")}>
          <span className="font-semibold">{monthName} {prevYear}</span> {reminderText}
          {gapDays > 0 && (
            <> Du har {gapDays} dag{gapDays !== 1 ? "er" : ""} med manglende timer.</>
          )}
        </p>
      </div>
      <Button
        size="sm"
        variant={isAvailable ? "outline" : "default"}
        className="w-full md:w-auto min-h-[44px] md:min-h-0 flex-shrink-0"
        onClick={() =>
          navigate(
            `/mine-oppgaver/avslutt-maaned?year=${prevYear}&month=${prevMonth}`
          )
        }
      >
        Lukk måned →
      </Button>
    </div>
  );
}
